import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Loader2, PlayCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";

const age = (minutes) => {
  if (minutes == null) return "never";
  if (minutes < 60) return `${Math.round(minutes)} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} d ago`;
};

const cadence = (minutes) => {
  if (minutes < 60) return `every ${minutes} min`;
  if (minutes < 1440) return `every ${Math.round(minutes / 60)} h`;
  return "daily";
};

const summarise = (result) => {
  const body = result?.last_result || {};
  const parts = [];
  const walk = (value, prefix = "") => {
    Object.entries(value || {}).forEach(([key, v]) => {
      if (parts.length >= 4 || key === "at") return;
      if (Array.isArray(v)) parts.push(`${prefix}${key} ${v.length}`);
      else if (typeof v === "number") parts.push(`${prefix}${key} ${v}`);
      else if (typeof v === "boolean") parts.push(`${prefix}${key} ${v ? "yes" : "no"}`);
      else if (v && typeof v === "object") walk(v, `${key}·`);
    });
  };
  walk(body);
  return parts.join(" · ") || "nothing recorded";
};

export const CronFailureBanner = () => {
  const { can } = useAuth();
  const { t } = useTranslation();
  const canRead = can("user", "read");
  const [failed, setFailed] = useState([]);

  useEffect(() => {
    if (!canRead) return;
    api.get("/admin/cron-health")
      .then(({ data }) => setFailed(data.failed || []))
      .catch(() => {});
  }, [canRead]);

  if (!failed.length) return null;
  return (
    <div data-testid="cron-failure-banner"
         className="flex items-start gap-2.5 rounded-xl border border-red-500/40 bg-red-500/10 px-3.5 py-2.5">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-[12.5px] text-red-700 dark:text-red-300">
        {t("The last run of")} <b data-testid="cron-failure-jobs">{failed.join(", ")}</b> {t("failed. Open the Scheduled jobs tab to read the error and run it again by hand.")}
      </p>
    </div>
  );
};

export const CronHealth = () => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/cron-health");
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); setData({ jobs: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async (name) => {
    setBusy(name);
    try {
      await api.post(`/admin/cron-health/${name}/run`);
      toast.success(`${name} ran now`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return null;
  const writable = can("user", "write");

  return (
    <Panel testId="panel-cron-health" kicker="Scheduled jobs"
           title="What runs on its own, when it last ran and what it did"
           actions={
             <div className="flex items-center gap-2">
               <span className="text-[11px] text-muted-foreground" data-testid="cron-health-count">
                 {data.jobs.filter((j) => j.scheduled).length}/{data.limit} slots used
               </span>
               <Button size="sm" variant="outline" className="h-8 rounded-full text-[11px]"
                       onClick={load} data-testid="cron-health-refresh">
                 <RefreshCw className="mr-1 h-3 w-3" /> Refresh
               </Button>
             </div>
           }>
      <div className="space-y-3 p-4">
        {data.problem && (
          <p className="rounded-xl border border-rose-500/30 bg-rose-500/8 p-3 text-[12px] text-rose-600 dark:text-rose-400"
             data-testid="cron-health-problem">
            {data.problem}
          </p>
        )}
        {data.silent?.length > 0 && (
          <p className="rounded-xl border border-amber-500/30 bg-amber-500/8 p-3 text-[12px] text-amber-600 dark:text-amber-400"
             data-testid="cron-health-silent">
            Gone quiet: {data.silent.join(", ")}. Run it by hand to see what happens, or check the
            backend log.
          </p>
        )}
        {data.ignored?.length > 0 && (
          <p className="rounded-xl border border-border bg-muted/40 p-3 text-[12px] text-muted-foreground"
             data-testid="cron-health-ignored">
            The platform only schedules the first {data.limit} jobs, so these are never run:
            {" "}{data.ignored.join(", ")}.
          </p>
        )}

        <Table testId="cron-health-table"
               head={<><Th>Job</Th><Th>Runs</Th><Th>Last run</Th><Th>What it did</Th>
                 <Th className="text-right">State</Th></>}>
          {data.jobs.map((j) => (
            <tr key={j.name} className="row-hover" data-testid={`cron-row-${j.name}`}>
              <Td>
                <div className="font-medium text-foreground">{j.name}</div>
                <div className="max-w-[340px] text-[11px] text-muted-foreground">{j.description}</div>
              </Td>
              <Td className="whitespace-nowrap">
                <div className="text-[12px] text-foreground">{cadence(j.expected_every_minutes)}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">
                  {j.cron} {j.timezone !== "UTC" ? j.timezone : "UTC"}
                </div>
              </Td>
              <Td className="whitespace-nowrap">
                <div className="text-[12px] text-foreground" data-testid={`cron-age-${j.name}`}>
                  {age(j.last_age_minutes)}
                </div>
                <div className="text-[10.5px] text-muted-foreground">
                  {j.last_run_at ? dateTime(j.last_run_at) : "no record yet"}
                </div>
              </Td>
              <Td>
                <div className="max-w-[320px] truncate font-mono text-[11px] text-muted-foreground"
                     title={summarise(j)} data-testid={`cron-result-${j.name}`}>
                  {j.last_error ? `error: ${j.last_error}` : summarise(j)}
                </div>
                <div className="text-[10.5px] text-muted-foreground">{j.deliveries} deliveries logged</div>
              </Td>
              <Td className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {!j.scheduled ? (
                    <Chip className="border-border bg-muted text-muted-foreground"
                          testId={`cron-state-${j.name}`}>not scheduled</Chip>
                  ) : j.failed ? (
                    <Chip className="border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400"
                          testId={`cron-state-${j.name}`}>
                      <AlertTriangle className="h-3 w-3" /> failed
                    </Chip>
                  ) : j.silent ? (
                    <Chip className="border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                          testId={`cron-state-${j.name}`}>
                      <AlertTriangle className="h-3 w-3" /> quiet
                    </Chip>
                  ) : (
                    <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                          testId={`cron-state-${j.name}`}>
                      <CheckCircle2 className="h-3 w-3" /> healthy
                    </Chip>
                  )}
                  {writable && j.can_run_now && (
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]"
                            disabled={busy === j.name} onClick={() => runNow(j.name)}
                            data-testid={`cron-run-${j.name}`}>
                      {busy === j.name ? <Loader2 className="h-3 w-3 animate-spin" />
                                       : <PlayCircle className="h-3 w-3" />}
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>

        <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          Checked {data.checked_at ? dateTime(data.checked_at) : "just now"}
          {data.secret_configured ? " · the scheduler's secret is set"
                                  : " · warning: no scheduler secret is configured"}
        </p>
      </div>
    </Panel>
  );
};
